import { useEffect, useRef, useState, type ReactNode } from "react";

import { useReducedMotion } from "@/hooks/use-reduced-motion";

type Animation = "fadeInUp" | "fadeInUpSoft" | "fadeInScale" | "fadeInLeft" | "fadeInRight";

interface AnimatedRevealProps {
  children: ReactNode;
  animation?: Animation;
  duration?: number;
  delay?: number;
  easing?: string;
  threshold?: number;
  className?: string;
  style?: React.CSSProperties;
}

const animationKeyframes: Record<Animation, string> = {
  fadeInUp: `from { opacity: 0; transform: translate3d(0, 24px, 0); }
             to   { opacity: 1; transform: translate3d(0, 0, 0); }`,
  fadeInUpSoft: `from { opacity: 0; transform: translate3d(0, 16px, 0); }
                 to   { opacity: 1; transform: translate3d(0, 0, 0); }`,
  fadeInScale: `from { opacity: 0; transform: scale(0.96) translate3d(0, 12px, 0); }
                to   { opacity: 1; transform: scale(1) translate3d(0, 0, 0); }`,
  fadeInLeft: `from { opacity: 0; transform: translate3d(-20px, 0, 0); }
               to   { opacity: 1; transform: translate3d(0, 0, 0); }`,
  fadeInRight: `from { opacity: 0; transform: translate3d(20px, 0, 0); }
                to   { opacity: 1; transform: translate3d(0, 0, 0); }`,
};

let styleInjected = false;
function injectKeyframes() {
  if (styleInjected) return;
  styleInjected = true;
  const style = document.createElement("style");
  style.textContent = Object.entries(animationKeyframes)
    .map(([name, frames]) => `@keyframes ${name} { ${frames} }`)
    .join("\n");
  document.head.appendChild(style);
}

export function AnimatedReveal({
  children,
  animation = "fadeInUp",
  duration = 600,
  delay = 0,
  easing = "cubic-bezier(0.16, 1, 0.3, 1)",
  threshold = 0.1,
  className = "",
  style,
}: AnimatedRevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  // MS184: shared useReducedMotion hook. Reactive — live OS-pref
  // toggle now flips this surface's intro animation off/on. The
  // effect deps include it so re-mounting fires the right branch.
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    injectKeyframes();

    const el = ref.current;
    if (!el) return;

    if (reducedMotion) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.willChange = "transform, opacity";
          setVisible(true);
          observer.disconnect();
          // Clean up will-change after animation
          setTimeout(() => {
            if (el) el.style.willChange = "auto";
          }, duration + delay + 100);
        }
      },
      { threshold },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [duration, delay, threshold, reducedMotion]);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        ...style,
        opacity: visible ? undefined : 0,
        animation: visible
          ? `${animation} ${duration}ms ${easing} ${delay}ms both`
          : undefined,
      }}
    >
      {children}
    </div>
  );
}
